import React from 'react'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableCell,
  TableRow,
  StripedRow,
} from '~/lib/ui/table'
import Pagination from './Pagination'
import { useCandidateActivity } from './CandidateActivitySection'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/lib/ui/dialog'
import { Button } from '~/lib/ui/button'
import moment from 'moment'
import 'moment-timezone'
import NewSpecialEvent from './NewSpecialEvent'
import { useCandidate } from './CandidateProvider'

const PAGE_SIZE = 50

const SpecialEvents = () => {
  const { candidateSpecialEvents, postLandingSupport, refetch } =
    useCandidateActivity()
  const [page, setPage] = React.useState(1)
  const total = candidateSpecialEvents?.length || 0
  const { candidateId } = useCandidate()

  // Paginate activities
  const paginated = React.useMemo(() => {
    if (!candidateSpecialEvents) return []
    const start = (page - 1) * PAGE_SIZE
    return candidateSpecialEvents.slice(start, start + PAGE_SIZE)
  }, [candidateSpecialEvents, page])

  return (
    <>
      <div className="mb-6 flex w-full justify-center">
        <div className="flex gap-4">
          <NewSpecialEvent
            event="interview"
            candidateId={candidateId}
            onSuccess={refetch}
          >
            Got an Interview
          </NewSpecialEvent>
          <NewSpecialEvent
            event="offer"
            candidateId={candidateId}
            onSuccess={refetch}
          >
            Got an Offer
          </NewSpecialEvent>
          <NewSpecialEvent
            event="hired"
            candidateId={candidateId}
            onSuccess={refetch}
          >
            Successful Transition
          </NewSpecialEvent>
          <NewSpecialEvent
            event="feedback"
            candidateId={candidateId}
            onSuccess={refetch}
          >
            Service Feedback
          </NewSpecialEvent>
          {postLandingSupport && (
            <NewSpecialEvent
              event="post_landing_support"
              candidateId={candidateId}
            >
              Post Landing Support
            </NewSpecialEvent>
          )}
        </div>
      </div>

      <Table
        className="w-full border-separate"
        tableClass="w-full rounded-2xl border border-silver"
      >
        <SpecialEventsHeading />
        <TableBody>
          {paginated.map((candidateSpecialEvent) => (
            <SpecialEventsRow
              key={`candidate-job-alerts-${candidateSpecialEvent.id}`}
              candidateSpecialEvent={candidateSpecialEvent}
            />
          ))}
        </TableBody>
      </Table>
      {total > PAGE_SIZE && (
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
        />
      )}
    </>
  )
}

export default SpecialEvents

const SpecialEventsRow = ({ candidateSpecialEvent, ...props }) => {
  const { event_type, note, created_at, author, type } =
    candidateSpecialEvent || {}
  const displayTime = moment.tz(created_at, moment.tz.guess()).format('LLL z')

  let eventName
  if (type === 'Feedback::GotHired' || event_type === 'hired') {
    eventName = 'Got Hired'
  } else {
    eventName = `${titleize(event_type)}`
  }

  return (
    <TableRow
      {...props}
      className="odd:bg-white even:bg-[#e2e2e2] even:bg-opacity-[29%]"
    >
      <TableCell>{eventName}</TableCell>
      <TableCell>{author}</TableCell>
      <TableCell>{displayTime}</TableCell>
      <TableCell>{note || 'N/A'}</TableCell>
      <TableCell>
        <ServiceRequestDetails candidateSpecialEvent={candidateSpecialEvent}>
          View
        </ServiceRequestDetails>
      </TableCell>
    </TableRow>
  )
}

const SpecialEventsHeading = () => {
  return (
    <TableHeader className="h-6 bg-intoo-blue-medium">
      <TableRow className="text-lg text-white hover:bg-intoo-blue-medium">
        {TABLE_HEADER_CELLS.map((cell, index) => (
          <TableHead key={index} className="font-medium text-white">
            {cell}
          </TableHead>
        ))}
      </TableRow>
    </TableHeader>
  )
}

function titleize(str) {
  return (str || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

const TABLE_HEADER_CELLS = ['Event Name', 'Author', 'Time', 'Note', 'Action']

const ServiceRequestDetails = ({ candidateSpecialEvent, children }) => {
  const [open, setOpen] = React.useState(false)
  const { content, hired_by, event_type, feedback, type } =
    candidateSpecialEvent || {}
  const { location, notes, role, salary } = content || {}

  let eventHeading = ''
  if (type === 'Feedback::GotHired') {
    eventHeading = 'I Got Hired'
  } else if (event_type && event_type !== 'feedback') {
    eventHeading = `I Got ${titleize(event_type)}`
  } else {
    eventHeading = `Service ${titleize(event_type)}`
  }

  const rows = []
  if (type === 'Feedback::GotHired') {
    rows.push(
      { label: 'Hired At', value: hired_by || '' },
      { label: 'Was Our Site Helpful?', value: feedback || '' },
    )
  } else if (event_type && event_type !== 'feedback') {
    rows.push(
      { label: 'Company Name', value: hired_by || '' },
      { label: 'Location', value: location || '' },
      { label: 'Role', value: role || '' },
      { label: 'Salary', value: salary || '' },
    )
  }
  if (type !== 'Feedback::GotHired') {
    rows.push({
      label: 'Notes',
      value: notes || feedback || '',
    })
  }

  return (
    <Dialog
      open={open}
      onOpenChange={() => setOpen(!open)}
      className="rounded-2xl"
    >
      <DialogTrigger asChild className="cursor-pointer">
        <Button
          variant="link"
          className="m-0 justify-start p-0 font-normal text-intoo-blue-medium hover:text-link"
        >
          {children}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="font-roboto lg:max-w-3xl xl:max-w-5xl"
        variant="default"
        dialogCloseClass="text-black"
      >
        <DialogHeader>
          <DialogTitle className="w-full text-center" asChild>
            <h2 className="w-full text-2xl">Service Event Details</h2>
          </DialogTitle>
        </DialogHeader>
        <h3 className="text-center text-lg font-semibold">{eventHeading}</h3>
        <div className="flex w-full justify-center px-6 pb-6">
          <Table
            className="w-full border-separate"
            tableClass="rounded-2xl border border-silver"
          >
            <TableBody>
              {rows.map(({ label, value }) => (
                <StripedRow key={label}>
                  <TableCell className="w-44 pr-2 text-left font-semibold">
                    {label}
                  </TableCell>
                  <TableCell className="w-2 border-r px-2 text-center" />
                  <TableCell className="pl-2 text-left">{value}</TableCell>
                </StripedRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  )
}
